// Phase SIKA-4b — Verified Return Certificate composition.
//
// Source: SIKA_Independence_Architecture_v1.0 §12 Table 24 row 19.
//
// Composes I0 primitives only (Eyebrow, Stat, Pill, HairlineRow).
// No primitive edits. No --sa-* token mutations.
//
// Pro feature per Table 22 — free users see header + total + completion
// date + "View full certificate · Pro" CTA. Paid users see the full
// manifest summary + tamper-proof SHA-256 Pill.

import type { CSSProperties } from "react";
import { Eyebrow, HairlineRow, Pill, Stat } from "../super-acca";
import { useSikaCertificate } from "../../sika/useSikaCertificate.ts";

export interface VerifiedReturnCertificateProps {
  goalId: string;
  /** Pro entitlement flag — when false, the panel is gated to summary + CTA. */
  isPaid: boolean;
  style?: CSSProperties;
}

interface CertificateManifest {
  goal_name: string;
  target_amount: number;
  final_amount: number;
  total_banked: number;
  currency: string;
  created_at: string;
  completed_at: string | null;
  cycles?: unknown[];
  milestones?: unknown[];
  summary?: {
    total_legs: number;
    legs_won: number;
    legs_lost: number;
    cycles_skipped: number;
    weeks_to_completion: number;
    win_rate: number;
  };
}

function formatGHS(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `GH₵${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

function shortHash(hash: string): string {
  if (!hash) return "—";
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
      timeZone: "Africa/Accra",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export function VerifiedReturnCertificate({
  goalId,
  isPaid,
  style,
}: VerifiedReturnCertificateProps): JSX.Element {
  const { certificate, loading, error } = useSikaCertificate(goalId);

  const cardStyle: CSSProperties = {
    border: "1px solid var(--sa-hairline)",
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 14,
    background: "var(--sa-bg-surface, transparent)",
    ...style,
  };

  if (loading) {
    return (
      <div style={cardStyle} data-testid="vrc-loading">
        <Eyebrow>STRIKZA · VERIFIED RETURN CERTIFICATE</Eyebrow>
        <div style={{ fontFamily: "var(--sa-font-mono)", fontSize: 12, color: "var(--sa-fg-muted)" }}>
          Loading certificate…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={cardStyle} data-testid="vrc-error">
        <Eyebrow>STRIKZA · VERIFIED RETURN CERTIFICATE</Eyebrow>
        <div style={{ fontFamily: "var(--sa-font-mono)", fontSize: 12, color: "var(--sa-fg-muted)" }}>
          Certificate unavailable: {error}
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div style={cardStyle} data-testid="vrc-empty">
        <Eyebrow>STRIKZA · VERIFIED RETURN CERTIFICATE</Eyebrow>
        <div style={{ fontFamily: "var(--sa-font-mono)", fontSize: 12, color: "var(--sa-fg-muted)" }}>
          Your certificate generates automatically when the goal completes.
        </div>
      </div>
    );
  }

  const manifest = certificate.manifest as unknown as CertificateManifest;
  const summary = manifest.summary;

  return (
    <div style={cardStyle} data-testid="vrc-card">
      <Eyebrow>STRIKZA · VERIFIED RETURN CERTIFICATE</Eyebrow>
      <div
        style={{
          fontFamily: "var(--sa-font-stat)",
          fontSize: 24,
          letterSpacing: 1,
          color: "var(--sa-fg-primary)",
        }}
      >
        {manifest.goal_name}
      </div>
      <HairlineRow first columns="1fr 1fr">
        <Stat label="TOTAL BANKED" value={formatGHS(manifest.total_banked ?? manifest.final_amount)} display />
        <Stat label="COMPLETED" value={formatDate(manifest.completed_at)} />
      </HairlineRow>

      {isPaid ? (
        <>
          <HairlineRow columns="1fr 1fr 1fr">
            <Stat label="WEEKS" value={String(summary?.weeks_to_completion ?? "—")} />
            <Stat
              label="WINS / LOSSES"
              value={`${summary?.legs_won ?? 0} / ${summary?.legs_lost ?? 0}`}
            />
            <Stat label="WIN RATE" value={`${(summary?.win_rate ?? 0).toFixed(1)}%`} />
          </HairlineRow>
          <HairlineRow>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
              <div
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 11,
                  color: "var(--sa-fg-muted)",
                  letterSpacing: "0.08em",
                }}
              >
                MANIFEST HASH · {shortHash(certificate.manifest_hash)}
              </div>
              <Pill borderColor="var(--sa-hairline)">TAMPER-PROOF · SHA-256</Pill>
            </div>
          </HairlineRow>
        </>
      ) : (
        <HairlineRow>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 12,
                color: "var(--sa-fg-muted)",
                lineHeight: 1.5,
              }}
            >
              Your goal is permanently recorded. Unlock the full tamper-proof
              ledger — every cycle, every leg, every milestone — with SIKA Pro.
            </div>
            <Pill borderColor="var(--sa-accent-saffron)" color="var(--sa-accent-saffron)">
              VIEW FULL CERTIFICATE · PRO
            </Pill>
          </div>
        </HairlineRow>
      )}
    </div>
  );
}
